<template>
  <div class="tier-badge" :class="badgeClasses">
    <!-- Badge Icon -->
    <component
      v-if="showIcon"
      :is="iconComponent"
      class="tier-badge-icon"
      :class="iconClasses"
      aria-hidden="true"
    />

    <!-- Badge Label -->
    <span class="tier-badge-label" :class="labelClasses">
      {{ tierConfig.label }}
    </span>

    <!-- Optional suffix (e.g., "Plan") -->
    <span v-if="showPlan" class="tier-badge-suffix">
      Plan
    </span>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { TIER_DISPLAY } from '@/utils/constants/tiers'
import {
  DocumentTextIcon,
  ShieldExclamationIcon,
  UserIcon
} from '@heroicons/vue/24/outline'

// Mapeo de nombres de icono a componentes
const iconMap = {
  DocumentTextIcon,
  ShieldExclamationIcon,
  UserIcon
}

/**
 * TierBadge Component
 *
 * Implementing Component Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Visual indicator for user subscription tier following glassmorphism design
 *
 * Connected with:
 * - Auth store user.subscriptionTier
 * - TIER_DISPLAY configuration from tiers.js
 * - Dashboard components for tier visualization
 */

const props = defineProps({
  // User's current tier - Updated for new service model (Sprint 03)
  tier: {
    type: String,
    required: true,
    validator: (value) => ['olvido', 'olvido_postmortem', 'testamento'].includes(value)
  },

  // Badge size variant
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
  },

  // Display options
  showIcon: {
    type: Boolean,
    default: true
  },
  showPlan: {
    type: Boolean,
    default: false
  },

  // Style variant
  variant: {
    type: String,
    default: 'solid',
    validator: (value) => ['solid', 'gradient', 'outline', 'glass'].includes(value)
  },

  // Additional customization
  rounded: {
    type: Boolean,
    default: true
  },
  animated: {
    type: Boolean,
    default: false
  }
})

// Get tier configuration
const tierConfig = computed(() => TIER_DISPLAY[props.tier] || TIER_DISPLAY.basic)

// Get icon component based on tier
const iconComponent = computed(() => {
  const iconName = tierConfig.value.badgeIcon
  return iconMap[iconName] || DocumentTextIcon
})

// Badge container classes
const badgeClasses = computed(() => {
  const classes = [
    'inline-flex items-center font-medium transition-all duration-200',

    // Size variants
    {
      'px-2 py-1 text-xs gap-1': props.size === 'sm',
      'px-3 py-1.5 text-sm gap-1.5': props.size === 'md',
      'px-4 py-2 text-base gap-2': props.size === 'lg',
      'px-5 py-2.5 text-lg gap-2.5': props.size === 'xl'
    },

    // Rounded corners
    {
      'rounded-full': props.rounded && props.size !== 'xl',
      'rounded-2xl': props.rounded && props.size === 'xl'
    }
  ]

  // Style variants
  switch (props.variant) {
    case 'solid':
      classes.push(
        tierConfig.value.bgClass,
        tierConfig.value.borderClass,
        'border',
        tierConfig.value.textClass
      )
      break

    case 'gradient':
      classes.push(
        'bg-gradient-to-r',
        tierConfig.value.gradient,
        'text-white',
        'shadow-lg'
      )
      break

    case 'outline':
      classes.push(
        'bg-transparent',
        'border-2',
        tierConfig.value.borderClass,
        tierConfig.value.textClass
      )
      break

    case 'glass':
      classes.push(
        'bg-white',
        'border',
        'border-white/20',
        'shadow-lg',
        tierConfig.value.textClass
      )
      break
  }

  // Animation
  if (props.animated) {
    classes.push('hover:scale-105', 'hover:shadow-xl')
  }

  return classes
})

// Icon classes - Heroicons use w-/h- for sizing
const iconClasses = computed(() => {
  const classes = []

  // Size adjustments for Heroicons
  if (props.size === 'sm') {
    classes.push('w-3', 'h-3')
  } else if (props.size === 'md') {
    classes.push('w-4', 'h-4')
  } else if (props.size === 'lg') {
    classes.push('w-5', 'h-5')
  } else if (props.size === 'xl') {
    classes.push('w-6', 'h-6')
  } else {
    classes.push('w-4', 'h-4') // default
  }

  // Testamento tier animation (premium-like experience)
  if (props.tier === 'testamento' && props.animated) {
    classes.push('animate-pulse')
  }

  return classes
})

// Label classes
const labelClasses = computed(() => {
  const classes = ['font-semibold']

  // Gradient text for testamento in certain variants
  if (props.tier === 'testamento' && props.variant === 'outline') {
    classes.push('bg-gradient-to-r', 'from-amber-600', 'to-amber-400', 'bg-clip-text', 'text-transparent')
  }

  return classes
})
</script>

<style scoped>
@reference "../../assets/styles/main.css";

/* Smooth transitions */
.tier-badge {
  transition: all 0.2s ease-in-out;
}

/* Testamento glow effect */
.tier-badge[class*="testamento"][class*="gradient"] {
  box-shadow: 0 4px 14px 0 rgba(251, 191, 36, 0.3);
}

/* Post-mortem glow effect */
.tier-badge[class*="olvido_postmortem"][class*="gradient"] {
  box-shadow: 0 4px 14px 0 rgba(147, 51, 234, 0.3);
}

/* Olvido subtle shadow */
.tier-badge[class*="olvido"][class*="gradient"]:not([class*="postmortem"]) {
  box-shadow: 0 4px 14px 0 rgba(20, 184, 166, 0.3);
}

/* Hover elevations */
.tier-badge.hover\:scale-105:hover {
  transform: scale(1.05);
}

/* Glass effect enhancement */
.tier-badge[class*="glass"] {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

/* Premium pulse animation */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Tier-specific icon animations */
.tier-badge-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Suffix styling */
.tier-badge-suffix {
  opacity: 0.8;
  font-weight: normal;
  margin-left: 0.25rem;
}</style>